import React from "react";

import Text from "cms/components/Text";
import BackgroundPurpleGradientRight from "cms/images/Uizard_Purple_Darkv2.png";
import BackgroundGradientDarkPurpleFilled from "cms/images/background-gradient-dark-purple-filled.webp";
import BackgroundGradientDarkPurpleOutline from "cms/images/background-gradient-dark-purple-outline.webp";

import MediaQuery from "../MediaQuery";
import OptimizedImage from "../OptimizedImage";
import RichText from "../RichText";
import {
  BackgroundImageContainer,
  BackgroundImagePurpleFilled,
  BackgroundImagePurpleOutline,
  BackgroundSolidColorOrange,
  BackgroundSolidColorPurple,
  BentoCardBackground,
  BentoCardLightMode,
  BentoCardVariant,
  Container,
  DescriptionWrapper,
  IllustrationContainer,
  Message,
} from "./BentoCard.styles";

export interface BentoCardProps {
  lightMode: BentoCardLightMode;
  card: Queries.Maybe<Queries.SanityBentoCardItem>;
  className: string;
}

const BentoCard: React.FC<BentoCardProps> = ({
  card,
  lightMode,
  className,
}) => (
  <Container
    $variant={card?.variant as BentoCardVariant}
    $backgroundColor={card?.backgroundColor as BentoCardBackground}
    className={className}
    $cardName={className}
    $lightMode={lightMode}
  >
    <Message
      $variant={card?.variant as BentoCardVariant}
      $backgroundColor={card?.backgroundColor as BentoCardBackground}
      $textColor={lightMode}
      $cardName={className}
      $lightMode={lightMode}
    >
      <Text variant="H5" as="h5" color="inherit">
        {card?.title}
      </Text>

      {card?._rawCardDescription && card?.backgroundColor && (
        <DescriptionWrapper
          $background={card.backgroundColor}
          $lightMode={lightMode}
        >
          <RichText text={card._rawCardDescription} variant="B3" />
        </DescriptionWrapper>
      )}

      {card?.variant === "wide" &&
        (lightMode === "dark" || lightMode === "darkAutodesigner") && (
          <React.Fragment>
            {card?.imageWideTwo && (
              <MediaQuery
                $lessThan="large"
                style={{
                  width: "100%",
                }}
              >
                <IllustrationContainer
                  $addGradient
                  $cardName={className}
                  $lightMode={lightMode}
                >
                  <OptimizedImage
                    image={card.imageWideTwo}
                    alt={card?.imagesAlt ?? ""}
                  />
                </IllustrationContainer>
              </MediaQuery>
            )}
          </React.Fragment>
        )}

      {card?.variant === "wide" &&
        (lightMode === "dark" ||
          lightMode === "darkAutodesigner" ||
          lightMode === "darkHomepage") && (
          <React.Fragment>
            {card?.imageWideTwo && (
              <MediaQuery
                $greaterThan="large"
                style={{
                  margin: "1.5rem auto 0 auto",
                  width: "85%",
                }}
              >
                <IllustrationContainer
                  $addGradient
                  $cardName={className}
                  $lightMode={lightMode}
                >
                  <OptimizedImage
                    image={card.imageWideTwo}
                    alt={card?.imagesAlt ?? ""}
                  />
                </IllustrationContainer>
              </MediaQuery>
            )}
          </React.Fragment>
        )}
    </Message>

    <MediaQuery $lessThan="medium" style={{ height: "100%" }}>
      <IllustrationContainer $cardName={className} $lightMode={lightMode}>
        {card?.imageMobile && (
          <OptimizedImage
            image={card.imageMobile}
            alt={card?.imagesAlt ?? ""}
          />
        )}
      </IllustrationContainer>
    </MediaQuery>

    <MediaQuery
      $between={["medium", "large"]}
      style={{ width: "100%", height: "100%" }}
    >
      <IllustrationContainer $cardName={className} $lightMode={lightMode}>
        {card?.imageTablet && (
          <OptimizedImage
            image={card.imageTablet}
            alt={card?.imagesAlt ?? ""}
          />
        )}
      </IllustrationContainer>
    </MediaQuery>

    <MediaQuery
      $greaterThan="large"
      style={{
        alignSelf:
          className === "card7" && lightMode === "dark" ? "center" : "auto",
      }}
    >
      <IllustrationContainer $cardName={className} $lightMode={lightMode}>
        {card?.imageDesktop && (
          <OptimizedImage
            image={card.imageDesktop}
            alt={card?.imagesAlt ?? ""}
          />
        )}
      </IllustrationContainer>
    </MediaQuery>

    {card?.backgroundColor === "solidPurple" && <BackgroundSolidColorPurple />}

    {card?.backgroundColor === "solidOrange" && <BackgroundSolidColorOrange />}

    {card?.backgroundColor === "gradientPurple" && (
      <React.Fragment>
        <MediaQuery
          $lessThan="medium"
          style={{
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <BackgroundImageContainer>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="335"
              height="194"
              viewBox="0 0 335 194"
              fill="none"
            >
              <g filter="url(#filter0_f_605_13875)">
                <path
                  d="M173.556 120.54C88.7029 120.54 45.1883 195.34 40 222.847V274H640V237.324C633.975 213.195 573.222 182.31 535.063 170.728C435.474 140.501 267.481 120.54 173.556 120.54Z"
                  fill="#6419FF"
                />
              </g>
              <defs>
                <filter
                  id="filter0_f_605_13875"
                  x="-80"
                  y="0.540039"
                  width="840"
                  height="393.46"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood
                    floodOpacity="0"
                    result="BackgroundImageContainerFix"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="BackgroundImageContainerFix"
                    result="shape"
                  />
                  <feGaussianBlur
                    stdDeviation="60"
                    result="effect1_foregroundBlur_605_13875"
                  />
                </filter>
              </defs>
            </svg>
          </BackgroundImageContainer>
        </MediaQuery>

        <MediaQuery
          $between={["medium", "large"]}
          style={{
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <BackgroundImageContainer>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="728"
              height="194"
              viewBox="0 0 728 194"
              fill="none"
            >
              <g filter="url(#filter0_f_425_12315)">
                <path
                  d="M173.556 120.54C88.7029 120.54 45.1883 195.34 40 222.847V274H640V237.324C633.975 213.195 573.222 182.31 535.063 170.728C435.474 140.501 267.481 120.54 173.556 120.54Z"
                  fill="#6419FF"
                />
              </g>
              <defs>
                <filter
                  id="filter0_f_425_12315"
                  x="-80"
                  y="0.540039"
                  width="840"
                  height="393.46"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood
                    floodOpacity="0"
                    result="BackgroundImageContainerFix"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="BackgroundImageContainerFix"
                    result="shape"
                  />
                  <feGaussianBlur
                    stdDeviation="60"
                    result="effect1_foregroundBlur_425_12315"
                  />
                </filter>
              </defs>
            </svg>
          </BackgroundImageContainer>
        </MediaQuery>

        <MediaQuery
          $greaterThan="large"
          style={{
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <BackgroundImageContainer>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="725"
              height="274"
              viewBox="0 0 725 274"
              fill="none"
            >
              <g filter="url(#filter0_f_368_38982)">
                <path
                  d="M173.556 120.54C88.7029 120.54 45.1883 195.34 40 222.847V274H640V237.324C633.975 213.195 573.222 182.31 535.063 170.728C435.474 140.501 267.481 120.54 173.556 120.54Z"
                  fill="#6419FF"
                />
              </g>
              <defs>
                <filter
                  id="filter0_f_368_38982"
                  x="-80"
                  y="0.540039"
                  width="840"
                  height="393.46"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood
                    floodOpacity="0"
                    result="BackgroundImageContainerFix"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="BackgroundImageContainerFix"
                    result="shape"
                  />
                  <feGaussianBlur
                    stdDeviation="60"
                    result="effect1_foregroundBlur_368_38982"
                  />
                </filter>
              </defs>
            </svg>
          </BackgroundImageContainer>
        </MediaQuery>
      </React.Fragment>
    )}

    {card?.backgroundColor === "gradientPurpleFilled" && (
      <React.Fragment>
        <MediaQuery
          $lessThan="medium"
          style={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <BackgroundImageContainer>
            <BackgroundImagePurpleFilled
              src={BackgroundGradientDarkPurpleFilled}
              alt="Purple gradient"
            />
          </BackgroundImageContainer>
        </MediaQuery>

        <MediaQuery
          $between={["medium", "large"]}
          style={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <BackgroundImageContainer>
            <BackgroundImagePurpleFilled
              src={BackgroundGradientDarkPurpleFilled}
              alt="Purple gradient"
            />
          </BackgroundImageContainer>
        </MediaQuery>

        <MediaQuery
          $greaterThan="large"
          style={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <BackgroundImageContainer>
            <BackgroundImagePurpleFilled
              src={BackgroundGradientDarkPurpleFilled}
              alt="Purple gradient"
            />
          </BackgroundImageContainer>
        </MediaQuery>
      </React.Fragment>
    )}

    {card?.backgroundColor === "gradientPurpleOutline" && (
      <React.Fragment>
        <MediaQuery
          $lessThan="medium"
          style={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <BackgroundImageContainer>
            <BackgroundImagePurpleOutline
              src={BackgroundGradientDarkPurpleOutline}
              alt="Purple gradient"
            />
          </BackgroundImageContainer>
        </MediaQuery>

        <MediaQuery
          $between={["medium", "large"]}
          style={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <BackgroundImageContainer>
            <BackgroundImagePurpleOutline
              src={BackgroundGradientDarkPurpleOutline}
              alt="Purple gradient"
              $lightMode={lightMode}
            />
          </BackgroundImageContainer>
        </MediaQuery>

        <MediaQuery
          $greaterThan="large"
          style={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <BackgroundImageContainer>
            <BackgroundImagePurpleOutline
              src={BackgroundGradientDarkPurpleOutline}
              alt="Purple gradient"
            />
          </BackgroundImageContainer>
        </MediaQuery>
      </React.Fragment>
    )}

    {card?.backgroundColor === "gradientBlue" && (
      <React.Fragment>
        <MediaQuery
          $lessThan="medium"
          style={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <BackgroundImageContainer>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="335"
              height="268"
              viewBox="0 0 335 268"
              fill="none"
            >
              <g filter="url(#filter0_f_605_13902)">
                <path
                  d="M82.8293 120C-21.2577 120 -74.6357 211.635 -81 245.334V308H655V263.069C647.609 233.509 573.085 195.673 526.277 181.484C404.115 144.454 198.044 120 82.8293 120Z"
                  fill="#1E90FF"
                />
              </g>
              <defs>
                <filter
                  id="filter0_f_605_13902"
                  x="-201"
                  y="0"
                  width="976"
                  height="428"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood
                    floodOpacity="0"
                    result="BackgroundImageContainerFix"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="BackgroundImageContainerFix"
                    result="shape"
                  />
                  <feGaussianBlur
                    stdDeviation="60"
                    result="effect1_foregroundBlur_605_13902"
                  />
                </filter>
              </defs>
            </svg>
          </BackgroundImageContainer>
        </MediaQuery>

        <MediaQuery
          $between={["medium", "large"]}
          style={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <BackgroundImageContainer>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="352"
              height="268"
              viewBox="0 0 352 268"
              fill="none"
            >
              <g filter="url(#filter0_f_432_14814)">
                <path
                  d="M82.8293 120C-21.2577 120 -74.6357 211.635 -81 245.334V308H655V263.069C647.609 233.509 573.085 195.673 526.277 181.484C404.115 144.454 198.044 120 82.8293 120Z"
                  fill="#1E90FF"
                />
              </g>
              <defs>
                <filter
                  id="filter0_f_432_14814"
                  x="-201"
                  y="0"
                  width="976"
                  height="428"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood
                    floodOpacity="0"
                    result="BackgroundImageContainerFix"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="BackgroundImageContainerFix"
                    result="shape"
                  />
                  <feGaussianBlur
                    stdDeviation="60"
                    result="effect1_foregroundBlur_432_14814"
                  />
                </filter>
              </defs>
            </svg>
          </BackgroundImageContainer>
        </MediaQuery>

        <MediaQuery
          $greaterThan="large"
          style={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <BackgroundImageContainer>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="618"
              height="268"
              viewBox="0 0 618 268"
              fill="none"
            >
              <g filter="url(#filter0_f_368_39159)">
                <path
                  d="M82.8293 120C-21.2577 120 -74.6357 211.635 -81 245.334V308H655V263.069C647.609 233.509 573.085 195.673 526.277 181.484C404.115 144.454 198.044 120 82.8293 120Z"
                  fill="#1E90FF"
                />
              </g>
              <defs>
                <filter
                  id="filter0_f_368_39159"
                  x="-201"
                  y="0"
                  width="976"
                  height="428"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood
                    floodOpacity="0"
                    result="BackgroundImageContainerFix"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="BackgroundImageContainerFix"
                    result="shape"
                  />
                  <feGaussianBlur
                    stdDeviation="60"
                    result="effect1_foregroundBlur_368_39159"
                  />
                </filter>
              </defs>
            </svg>
          </BackgroundImageContainer>
        </MediaQuery>
      </React.Fragment>
    )}

    {card?.backgroundColor === "gradientPeach" && (
      <React.Fragment>
        <MediaQuery
          $lessThan="medium"
          style={{
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <BackgroundImageContainer>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="335"
              height="325"
              viewBox="0 0 335 325"
              fill="none"
            >
              <g filter="url(#filter0_f_605_13919)">
                <path
                  d="M666.244 120C784.897 120 845.745 224.796 853 263.334V335H14V283.616C22.4251 249.811 107.378 206.541 160.737 190.314C299.996 147.966 534.905 120 666.244 120Z"
                  fill="#FFC600"
                />
              </g>
              <defs>
                <filter
                  id="filter0_f_605_13919"
                  x="-106"
                  y="0"
                  width="1079"
                  height="455"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood
                    floodOpacity="0"
                    result="BackgroundImageContainerFix"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="BackgroundImageContainerFix"
                    result="shape"
                  />
                  <feGaussianBlur
                    stdDeviation="60"
                    result="effect1_foregroundBlur_605_13919"
                  />
                </filter>
              </defs>
            </svg>
          </BackgroundImageContainer>
        </MediaQuery>

        <MediaQuery
          $between={["medium", "large"]}
          style={{
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <BackgroundImageContainer>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="352"
              height="315"
              viewBox="0 0 352 315"
              fill="none"
            >
              <g filter="url(#filter0_f_425_12358)">
                <path
                  d="M676.244 120C794.897 120 855.745 224.796 863 263.334V335H24V283.616C32.4251 249.811 117.378 206.541 170.737 190.314C309.996 147.966 544.905 120 676.244 120Z"
                  fill="#FFC600"
                />
              </g>
              <defs>
                <filter
                  id="filter0_f_425_12358"
                  x="-96"
                  y="0"
                  width="1079"
                  height="455"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood
                    floodOpacity="0"
                    result="BackgroundImageContainerFix"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="BackgroundImageContainerFix"
                    result="shape"
                  />
                  <feGaussianBlur
                    stdDeviation="60"
                    result="effect1_foregroundBlur_425_12358"
                  />
                </filter>
              </defs>
            </svg>
          </BackgroundImageContainer>
        </MediaQuery>

        <MediaQuery
          $greaterThan="large"
          style={{
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <BackgroundImageContainer>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="618"
              height="287"
              viewBox="0 0 618 287"
              fill="none"
            >
              <g filter="url(#filter0_f_368_39045)">
                <path
                  d="M146.131 120C53.9238 120 6.63794 201.4 1 231.334V287H653V247.088C646.453 220.83 580.434 187.22 538.968 174.616C430.748 141.723 248.196 120 146.131 120Z"
                  fill="#FFD342"
                />
              </g>
              <defs>
                <filter
                  id="filter0_f_368_39045"
                  x="-119"
                  y="0"
                  width="892"
                  height="407"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood
                    floodOpacity="0"
                    result="BackgroundImageContainerFix"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="BackgroundImageContainerFix"
                    result="shape"
                  />
                  <feGaussianBlur
                    stdDeviation="60"
                    result="effect1_foregroundBlur_368_39045"
                  />
                </filter>
              </defs>
            </svg>
          </BackgroundImageContainer>
        </MediaQuery>
      </React.Fragment>
    )}
  </Container>
);

export default BentoCard;
